import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Typography, Button, CircularProgress, Alert, TextField, Badge } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SendIcon from '@mui/icons-material/Send';
import api from '../../services/api';
import { fmtDateTZ } from '../../utils/date';

// Messagerie « Aide Usafiri » : conversations ouvertes par les clients depuis
// l'app (Compte → Écrire à l'aide Usafiri). Edge function `chat`, rôle aide.
const POLL_MS = 8000;

const chat = (action, params = {}) => api.post('/chat', { app: 'admin', action, ...params }).then((r) => r.data);

export default function SupportChatTab({ P }) {
  const [list, setList] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [thread, setThread] = useState(null); // { conversation, messages }
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const lastRead = useRef(0);

  const loadList = useCallback(async () => {
    try {
      const data = await chat('inbox');
      setList(data.conversations || []);
    } catch (e) {
      setError(e.response?.data?.error || 'Impossible de charger les conversations.');
    } finally {
      setLoadingList(false);
    }
  }, []);

  const loadThread = useCallback(async (id) => {
    if (!id) return;
    try {
      const data = await chat('open', { conversationId: id });
      setThread(data);
      // Marque comme lu le dernier message du client.
      const latest = (data.messages || []).find((m) => m.role === 'customer');
      if (latest && latest.id > lastRead.current) {
        lastRead.current = latest.id;
        chat('read', { conversationId: id, messageId: latest.id }).then(loadList).catch(() => {});
      }
    } catch (e) {
      setError(e.response?.data?.error || 'Conversation indisponible.');
    }
  }, [loadList]);

  useEffect(() => {
    loadList();
    const t = setInterval(loadList, POLL_MS * 2);
    return () => clearInterval(t);
  }, [loadList]);

  useEffect(() => {
    if (!openId) return undefined;
    lastRead.current = 0;
    setThread(null);
    loadThread(openId);
    const t = setInterval(() => loadThread(openId), POLL_MS);
    return () => clearInterval(t);
  }, [openId, loadThread]);

  const send = async () => {
    const body = text.trim();
    if (!body || !openId || sending) return;
    setSending(true);
    setError(null);
    try {
      await chat('send', { conversationId: openId, body });
      setText('');
      await loadThread(openId);
      loadList();
    } catch (e) {
      setError(e.response?.data?.error || "Message non envoyé.");
    } finally {
      setSending(false);
    }
  };

  const unreadTotal = list.reduce((n, c) => n + (c.unread || 0), 0);
  const messages = thread ? [...(thread.messages || [])].reverse() : [];

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Box>
          <Typography sx={{ color: P.text, fontWeight: 700 }}>Aide Usafiri — messages des clients</Typography>
          <Typography sx={{ color: P.soft, fontSize: '0.78rem', mt: 0.5 }}>
            {unreadTotal} message{unreadTotal > 1 ? 's' : ''} non lu{unreadTotal > 1 ? 's' : ''} · actualisation automatique
          </Typography>
        </Box>
        <Button onClick={loadList} startIcon={<RefreshIcon />} size="small" sx={{ color: P.soft, textTransform: 'none' }}>
          Actualiser
        </Button>
      </Box>

      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}

      <Box display="flex" gap={2} sx={{ minHeight: 480, flexDirection: { xs: 'column', md: 'row' } }}>
        {/* Liste des conversations */}
        <Box sx={{ width: { xs: '100%', md: 320 }, flexShrink: 0, border: `1px solid ${P.line}`, borderRadius: 2, overflow: 'auto', maxHeight: 640 }}>
          {loadingList ? (
            <Box display="flex" justifyContent="center" py={6}><CircularProgress size={22} sx={{ color: P.gold }} /></Box>
          ) : list.length === 0 ? (
            <Typography sx={{ color: P.soft, py: 6, px: 2, textAlign: 'center', fontSize: '0.85rem' }}>
              Aucun message pour l'instant.
            </Typography>
          ) : (
            list.map((c) => (
              <Box
                key={c.conversation_id}
                onClick={() => setOpenId(c.conversation_id)}
                sx={{
                  px: 2, py: 1.5, cursor: 'pointer', borderBottom: `1px solid ${P.lineFine}`,
                  bgcolor: openId === c.conversation_id ? 'rgba(253,185,39,0.08)' : 'transparent',
                  '&:hover': { bgcolor: 'rgba(29,21,48,0.04)' },
                }}
              >
                <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                  <Typography sx={{ color: P.text, fontWeight: 700, fontSize: '0.88rem' }} noWrap>
                    {c.customer_name || `Client n° ${c.conversation_id}`}
                  </Typography>
                  {c.unread > 0 && <Badge badgeContent={c.unread} color="error" sx={{ mr: 1 }} />}
                </Box>
                <Typography sx={{ color: P.soft, fontSize: '0.78rem' }} noWrap>
                  {c.last_role === 'support' ? 'Vous : ' : ''}{c.last_body || (c.last_has_image ? 'Photo' : '')}
                </Typography>
                {c.last_message_at && (
                  <Typography sx={{ color: P.muted, fontSize: '0.68rem' }}>
                    {fmtDateTZ(c.last_message_at, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                  </Typography>
                )}
              </Box>
            ))
          )}
        </Box>

        {/* Fil */}
        <Box sx={{ flex: 1, border: `1px solid ${P.line}`, borderRadius: 2, display: 'flex', flexDirection: 'column', minHeight: 480 }}>
          {!openId ? (
            <Box flex={1} display="flex" alignItems="center" justifyContent="center">
              <Typography sx={{ color: P.soft, fontSize: '0.85rem' }}>Choisis une conversation à gauche.</Typography>
            </Box>
          ) : !thread ? (
            <Box flex={1} display="flex" alignItems="center" justifyContent="center"><CircularProgress size={22} sx={{ color: P.gold }} /></Box>
          ) : (
            <>
              <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${P.line}` }}>
                <Typography sx={{ color: P.text, fontWeight: 700 }}>{thread.conversation.title}</Typography>
              </Box>
              <Box sx={{ flex: 1, overflow: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 1, maxHeight: 520 }}>
                {thread.hasMore && (
                  <Typography sx={{ color: P.muted, fontSize: '0.72rem', textAlign: 'center' }}>Messages plus anciens non affichés</Typography>
                )}
                {messages.map((m) => {
                  const mine = m.role === 'support';
                  return (
                    <Box key={m.id} sx={{ alignSelf: mine ? 'flex-end' : 'flex-start', maxWidth: '75%' }}>
                      <Box sx={{ bgcolor: mine ? P.purple : 'rgba(29,21,48,0.08)', color: P.text, px: 1.5, py: 1, borderRadius: 2 }}>
                        {m.imageUrl && (
                          <a href={m.imageUrl} target="_blank" rel="noreferrer">
                            <img src={m.imageUrl} alt="Envoyée par le client" style={{ maxWidth: 240, borderRadius: 8, display: 'block' }} />
                          </a>
                        )}
                        {m.body && <Typography sx={{ fontSize: '0.88rem', whiteSpace: 'pre-wrap' }}>{m.body}</Typography>}
                      </Box>
                      <Typography sx={{ color: P.muted, fontSize: '0.65rem', mt: 0.3, textAlign: mine ? 'right' : 'left' }}>
                        {fmtDateTZ(m.createdAt, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                        {mine && thread.conversation.otherReadId >= m.id ? ' · Vu' : ''}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
              <Box sx={{ p: 1.5, borderTop: `1px solid ${P.line}`, display: 'flex', gap: 1 }}>
                <TextField
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                  placeholder="Répondre au client… (Entrée pour envoyer, Maj+Entrée pour aller à la ligne)"
                  multiline
                  maxRows={5}
                  fullWidth
                  size="small"
                  inputProps={{ maxLength: 2000 }}
                  sx={{ '& .MuiInputBase-root': { color: P.text, bgcolor: 'rgba(29,21,48,0.04)' } }}
                />
                <Button
                  onClick={send}
                  disabled={sending || !text.trim()}
                  variant="contained"
                  endIcon={sending ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <SendIcon />}
                  sx={{ bgcolor: P.gold, color: '#fff', fontWeight: 700, textTransform: 'none', borderRadius: '999px', '&:hover': { bgcolor: '#3f1a63' } }}
                >
                  Envoyer
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
}
